import Phaser from 'phaser';

export type Grid = Array<Array<number | null>>;
export type ImageGrid = Array<Array<Phaser.GameObjects.Image | null>>;

export default class Board {
    private scene: Phaser.Scene;
    private BOARD_CELL_SIZE_PX = 64;
    private X_SIZE = 4;
    private Y_SIZE = 4;
    private isDragging = false;
    private isClicking = false;
    private x0: number;
    private y0: number;

    private _gridArray: Grid;
    private imagesGridArray: ImageGrid = [[]];
    public boardContainer: Phaser.GameObjects.Container;
    public line?: Phaser.GameObjects.Line;
    private onClick: (x: number, y: number, clickedOnEmptyCell: boolean) => void;

    constructor({ scene, onClick, gridArray, scale }) {
        this.scene = scene;
        this._gridArray = gridArray;
        this.X_SIZE = gridArray[0].length;
        this.Y_SIZE = gridArray.length;
        this.onClick = onClick;
        this.x0 = -this.X_SIZE / 2 * this.BOARD_CELL_SIZE_PX + this.BOARD_CELL_SIZE_PX / 2 + this.scene.renderer.width / 2;
        this.y0 = -this.Y_SIZE / 2 * this.BOARD_CELL_SIZE_PX + this.BOARD_CELL_SIZE_PX / 2 + this.scene.renderer.height / 2;
        this.initBoard();
        this.render();
        this.boardContainer.setScale(scale);
    }

    public set gridArray(arr: Grid) {
        this._gridArray = arr;
        this.render();
    }

    public getImagesGrid() {
        return this.imagesGridArray;
    }

    get gridArray() {
        return this._gridArray;
    }

    private initBoard() {
        this.boardContainer = this.scene.add.container().setSize(this.scene.renderer.width, this.scene.renderer.height);

        for (let i = 0; i < this.Y_SIZE; i++) {
            for (let j = 0; j < this.X_SIZE; j++) {
                const x = this.x0 + j * this.BOARD_CELL_SIZE_PX;
                const y = this.y0 + i * this.BOARD_CELL_SIZE_PX;
                const rect = this.scene.add.rectangle(
                    x - 0.5,
                    y - 0.5,
                    this.BOARD_CELL_SIZE_PX - 1,
                    this.BOARD_CELL_SIZE_PX - 1
                );
                rect.setName(`${i}-${j}`);
                rect.isStroked = true;
                rect.strokeColor = 0x448aff;

                rect.setInteractive(new Phaser.Geom.Rectangle(0, 0, this.BOARD_CELL_SIZE_PX, this.BOARD_CELL_SIZE_PX), Phaser.Geom.Rectangle.Contains)
                this.scene.input.setDraggable(rect);
                this.scene.input.dragDistanceThreshold = 16;
                rect.on('pointerup', (e) => {
                    if (this.isDragging || !this.isClicking) return;
                    this.onClick(i, j, this.gridArray[i][j] === null || this.gridArray[i][j] === undefined);
                    this.isClicking = false;
                });
                rect.on('pointerdown', (e) => {
                    this.isClicking = true;
                });

                rect.on('dragstart', () => {
                    this.isDragging = true;
                });
                rect.on('drag', (pointer, dragX, dragY) => {
                    const deltaX = pointer.position.x - pointer.prevPosition.x;
                    const deltaY = pointer.position.y - pointer.prevPosition.y;
                    
                    this.boardContainer.setPosition(this.boardContainer.x + deltaX, this.boardContainer.y + deltaY);
                });
                rect.on('dragend', () => {
                    setTimeout(() => {
                        this.isDragging = false;
                    }, 10);
                });

                this.boardContainer.add([rect]);
                if (this.imagesGridArray[i]) {
                    this.imagesGridArray[i][j] = null;
                } else {
                    this.imagesGridArray[i] = [null];
                }
                
            }
        }
        this.boardContainer.setPosition(0,0);
        this.boardContainer.setInteractive(new Phaser.Geom.Rectangle(-2 * this.scene.renderer.width, -2 * this.scene.renderer.height, this.scene.renderer.width * 5, this.scene.renderer.height * 5), Phaser.Geom.Rectangle.Contains)
        this.boardContainer.on('pointerdown', function() {
            this.scene.selected = this;
            this.isClicking = true;
        });
        this.scene.input.on('pointermove', function(pointer) {
            const deltaX = pointer.position.x - pointer.prevPosition.x;
            const deltaY = pointer.position.y - pointer.prevPosition.y;
            
            if (this.scene.selected) {
                this.scene.selected.setPosition(this.scene.selected.x + deltaX, this.scene.selected.y + deltaY);
            }
        }, this);
        this.boardContainer.on('pointerup', function() {
            this.scene.selected = null;
        });
    }

    public restart() {
        this._gridArray.forEach((row, i) => {
            row.forEach((value, j) =>{
                this._gridArray[i][j] = null;
                this.imagesGridArray[i][j]?.destroy();
                this.imagesGridArray[i][j] = null;
            })
        });
        this.line?.destroy();
    }

    public renderCell(i: number, j: number) {
        const x = this.x0 + j * this.BOARD_CELL_SIZE_PX;
        const y = this.y0 + i * this.BOARD_CELL_SIZE_PX;

        if (this.gridArray[i][j] === null) {
            if (this.imagesGridArray[i][j]) {
                this.imagesGridArray[i][j]?.destroy(true);
            }
            this.imagesGridArray[i][j] = null;
        } else if (this.gridArray[i][j] === 0 && !this.imagesGridArray[i][j]) {
            this.imagesGridArray[i][j]?.destroy(true);
            const image = this.scene.add.image(0, 0, 'zero')
                .setOrigin(0.5)
                .setPosition(x, y)
                .setName('zero');
            this.imagesGridArray[i][j] = image;
            this.boardContainer.add([image]);
        } else if (this.gridArray[i][j] === 1 && !this.imagesGridArray[i][j]) {
            const image = this.scene.add.image(0, 0, 'cross')
                .setOrigin(0.5)
                .setPosition(x, y)
                .setName('cross');
            this.imagesGridArray[i][j] = image;
            this.boardContainer.add([image]);
        }
    }

    public render() {
        for (let i = 0; i < this.Y_SIZE; i++) {
            for (let j = 0; j < this.X_SIZE; j++) {
                this.renderCell(i, j);
            }
        }
    }
}